import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import './EventPreview.css';

const EventPreview = ({
  event = null,
  isOpen = false,
  position = { x: 0, y: 0 },
  onClose = () => {},
  onExpand = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onSave = null,
  onCreate = null,
  employees = [],
  // New props for event creation
  selectedDate = null,
  selectedHour = null,
  startTime = null,
  endTime = null,
  isCreating = false,
  prefilledParticipants = [],
  selectedEmployee = null,
  viewMode = 'week' // Add viewMode prop
}) => {
  
  console.log('🎭 EventPreview component received props:', {
    selectedHour,
    isCreating,
    viewMode,
    hasEvent: !!event,
    isOpen
  });

  console.log('🎭 EventPreview props:', { 
    isCreating, 
    prefilledParticipantsLength: prefilledParticipants?.length || 0, 
    prefilledParticipants,
    selectedDate,
    isOpen 
  });

  // Individual field editing states
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingParticipants, setEditingParticipants] = useState(false);

  // Editable field values
  const [editedTitle, setEditedTitle] = useState('');
  const [editedLocation, setEditedLocation] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');

  // Participants state
  const [participantSearch, setParticipantSearch] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showParticipantsDropdown, setShowParticipantsDropdown] = useState(false);

  // New state for description dropdown
  const [descriptionMode, setDescriptionMode] = useState('notes');

  // New state for expanded description options
  const [showDescriptionOptions, setShowDescriptionOptions] = useState(false);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const participantInputRef = useRef(null);

  // Zoom conferencing state
  const [zoomInfo, setZoomInfo] = useState(null);
  const [isCreatingZoom, setIsCreatingZoom] = useState(false);

  // Add ref for the main preview div to manage focus
  const previewRef = useRef(null);

  // Ensure the preview gets focus when it opens
  useEffect(() => {
    if (isOpen && previewRef.current) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        previewRef.current?.focus();
        console.log('🎭 EventPreview focused:', previewRef.current);
      }, 100);
    }
  }, [isOpen]);

  // File handling functions
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const handleFileUpload = useCallback((files) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      originalName: file.name
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Add download file function
  const downloadFile = useCallback(async (file, index) => {
    try {
      // Handle files that come from server (have URL)
      if (file.url || file.downloadUrl) {
        const link = document.createElement('a');
        link.href = file.url || file.downloadUrl;
        link.download = file.originalName || file.name || `file_${index}`;
        link.target = '_blank';
        // Add rel for security
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Handle server files with s3Key (need to fetch from backend)
      if (file.s3Key || file.id) {
        const token = localStorage.getItem('token');
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
        
        // Use the /download endpoint with query parameter - this is the most reliable approach
        let downloadUrl;
        if (file.s3Key) {
          // Use the download endpoint with the s3Key as a query parameter
          downloadUrl = `${API_BASE_URL}/api/files/download?key=${encodeURIComponent(file.s3Key)}`;
          console.log('🔑 Using download endpoint with s3Key query for file:', file.originalName || file.name);
        } else if (file.id) {
          // Use the file ID endpoint as fallback
          downloadUrl = `${API_BASE_URL}/api/files/${file.id}/download`;
          console.log('🆔 Using file ID approach for file:', file.originalName || file.name);
        } else {
          console.warn('File has neither ID nor s3Key:', file);
          throw new Error('Cannot download file: missing file ID and s3Key');
        }
        
        console.log('🔽 Downloading file from:', downloadUrl);
        
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
        }

        // Check if response is JSON (presigned URL) or binary data
        const contentType = response.headers.get('content-type');
        console.log('📋 Response content-type:', contentType);
        
        if (contentType && contentType.includes('application/json')) {
          // Backend returned a presigned URL
          const data = await response.json();
          console.log('📦 Backend response data:', data);
          
          if (data.url) {
            console.log('✅ Got presigned URL, downloading file directly');
            // Use a hidden iframe for smoother download experience
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = data.url;
            document.body.appendChild(iframe);
            
            // Clean up the iframe after a short delay
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
            }, 3000);
            
            console.log('✅ File download initiated via presigned URL:', file.originalName || file.name);
          } else {
            console.error('❌ No download URL in response:', data);
            throw new Error('No download URL received from backend');
          }
        } else {
          // Backend returned file content directly
          console.log('📦 Backend returned file content directly');
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.originalName || file.name || `file_${index}`;
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          URL.revokeObjectURL(url);
          document.body.removeChild(link);
          
          console.log('✅ File downloaded successfully:', file.originalName || file.name);
        }
        return;
      }

      // Handle newly uploaded files (have file blob)
      if (file.file) {
        const blob = new Blob([file.file], { type: file.type || file.file.type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = file.originalName || file.name || file.file.name || `file_${index}`;
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      // Fallback for other file structures
      if (file instanceof File) {
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || `file_${index}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      // If no recognized format, show error
      console.error('File format not supported for download:', file);
      alert('Unable to download this file. File format not supported.');
      
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Unable to download file. Please try again.');
    }
  }, []);

  // Add createZoomMeeting function
  const createZoomMeeting = async () => {
    if (!editedTitle.trim()) {
      alert('Please enter a title for the meeting.');
      return;
    }

    setIsCreatingZoom(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
      
      // Create proper Date objects from the event data
      let meetingStartTime, meetingEndTime;
      
      if (selectedDate && editedStartTime && editedEndTime) {
        // selectedDate is a Date object, editedStartTime and editedEndTime are strings like "14:00:00"
        const baseDate = new Date(selectedDate);
        
        // Handle time strings like "14:00:00"
        const [startHour, startMinute] = editedStartTime.split(':').map(Number);
        const [endHour, endMinute] = editedEndTime.split(':').map(Number);
        
        meetingStartTime = new Date(baseDate);
        meetingStartTime.setHours(startHour, startMinute, 0, 0);
        
        meetingEndTime = new Date(baseDate);
        meetingEndTime.setHours(endHour, endMinute, 0, 0);
        
        console.log('📅 Parsed meeting times:', {
          baseDate: baseDate.toISOString(),
          startTime: editedStartTime,
          endTime: editedEndTime,
          meetingStartTime: meetingStartTime.toISOString(),
          meetingEndTime: meetingEndTime.toISOString()
        });
      } else if (event) {
        // Use existing event data
        const eventDate = new Date(event.shift_date);
        if (event.start_time && event.end_time) {
          const [startHour, startMinute] = event.start_time.split(':').map(Number);
          const [endHour, endMinute] = event.end_time.split(':').map(Number);
          
          meetingStartTime = new Date(eventDate);
          meetingStartTime.setHours(startHour, startMinute, 0, 0);
          
          meetingEndTime = new Date(eventDate);
          meetingEndTime.setHours(endHour, endMinute, 0, 0);
        } else {
          // Fallback for all-day events
          meetingStartTime = new Date();
          meetingEndTime = new Date(meetingStartTime.getTime() + 30 * 60 * 1000);
        }
      } else {
        // Fallback to current time + 30 minutes
        meetingStartTime = new Date();
        meetingEndTime = new Date(meetingStartTime.getTime() + 30 * 60 * 1000);
        
        console.log('📅 Using fallback times:', {
          meetingStartTime: meetingStartTime.toISOString(),
          meetingEndTime: meetingEndTime.toISOString()
        });
      }

      const duration = Math.round((meetingEndTime - meetingStartTime) / (1000 * 60)); // Duration in minutes

      console.log('🎯 Creating Zoom meeting with:', {
        title: editedTitle,
        startTime: meetingStartTime.toISOString(),
        duration: duration + ' minutes'
      });

      const response = await fetch(`${API_BASE_URL}/api/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editedTitle,
          startTime: meetingStartTime.toISOString(),
          duration: duration
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Zoom API Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error(`Failed to create Zoom meeting: ${response.status} ${response.statusText}`);
      }

      const meetingData = await response.json();
      console.log('✅ Zoom meeting created successfully:', meetingData);

      // Store meeting info in state
      setZoomInfo({
        id: meetingData.id,
        passcode: meetingData.password,
        joinUrl: meetingData.join_url
      });
      
      console.log('🎉 Zoom meeting info stored in state');

      // Auto-save the zoom info to the event if it's an existing event
      if (event && onSave) {
        await onSave({ 
          ...event, 
          zoom_info: {
            id: meetingData.id,
            passcode: meetingData.password,
            joinUrl: meetingData.join_url
          }
        });
      }

    } catch (error) {
      console.error('❌ Error creating Zoom meeting:', error);
      alert(`Failed to create Zoom meeting: ${error.message}`);
    } finally {
      setIsCreatingZoom(false);
    }
  };

  // Initialize editable fields when event changes or when creating new event
  useEffect(() => {
    console.log('🎭 EventPreview main useEffect triggered with:', {
      event: event ? 'present' : 'null',
      isCreating,
      selectedDate: selectedDate ? 'present' : 'null',
      prefilledParticipants: prefilledParticipants ? prefilledParticipants.length : 'null'
    });

    console.log('🎭 EventPreview useEffect conditions check:', {
      hasEvent: !!event,
      isCreating: isCreating,
      hasSelectedDate: !!selectedDate,
      isCreatingAndHasDate: isCreating && selectedDate,
      willProcessExistingEvent: !!event,
      willProcessNewEvent: !event && isCreating && selectedDate
    });

    if (event) {
      console.log('🎭 EventPreview: Processing existing event');
      // Editing existing event
      setEditedTitle(event.title || '');
      setEditedLocation(event.location || '');
      setEditedDescription(event.description || '');
      setEditedStartTime(event.start_time || '');
      setEditedEndTime(event.end_time || '');
      
      // Load existing participants if any - check both locations for participant data
      let participantEmails = [];
      if (event.raw && event.raw.participants && Array.isArray(event.raw.participants)) {
        participantEmails = event.raw.participants;
      } else if (event.participants && Array.isArray(event.participants)) {
        participantEmails = event.participants;
      }
      
      if (participantEmails.length > 0 && employees.length > 0) {
        const participantObjects = participantEmails.map(email => {
          const employee = employees.find(emp => emp.email === email);
          return employee || { email, first_name: '', last_name: '' };
        });
        setSelectedParticipants(participantObjects);
      } else {
        setSelectedParticipants([]);
      }
      
      // Load existing files if any
      if (event.files && Array.isArray(event.files)) {
        setUploadedFiles(event.files);
      } else {
        setUploadedFiles([]);
      }

      // Load existing Zoom info if any
      if (event.zoom_info) {
        console.log('🎯 Loading existing Zoom info:', event.zoom_info);
        setZoomInfo(event.zoom_info);
      } else {
        console.log('🎯 No Zoom info found in existing event');
        setZoomInfo(null);
      }
    } else if (isCreating && selectedDate) {
      console.log('🎭 EventPreview: Processing new event creation');
      // Creating new event
      console.log('🎭 EventPreview creating new event with prefilledParticipants:', prefilledParticipants);
      setEditedTitle('');
      setEditedLocation('');
      setEditedDescription('');
      
      // Set default times based on props first, then selectedHour as fallback
      if (startTime && endTime) {
        // Use the correctly calculated times from the calendar
        setEditedStartTime(startTime);
        setEditedEndTime(endTime);
      } else if (selectedHour !== undefined) {
        if (selectedHour === -1) {
          // All-day event - explicitly set empty times to indicate all-day
          setEditedStartTime('');
          setEditedEndTime('');
        } else if (selectedHour >= 0) {
          // Fallback: Timed event calculated from selectedHour
          const hour = Math.floor(selectedHour);
          const minute = Math.round((selectedHour - hour) * 60);
          const endHour = hour + 1;
          
          const fallbackStartTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          const fallbackEndTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          
          setEditedStartTime(fallbackStartTime);
          setEditedEndTime(fallbackEndTime);
        }
      } else {
        // Default to empty times (all-day)
        setEditedStartTime('');
        setEditedEndTime('');
      }
      
      // Set participants from prefilledParticipants immediately for new events
      console.log('🎭 EventPreview setting selectedParticipants to:', prefilledParticipants || []);
      setSelectedParticipants(prefilledParticipants || []);
      // Auto-focus title for new events
      setEditingTitle(true);
      setUploadedFiles([]);
    } else {
      console.log('🎭 EventPreview: No conditions met for processing');
      console.log('🎭 EventPreview conditions check:', {
        hasEvent: !!event,
        isCreatingAndHasDate: isCreating && selectedDate,
        isCreating: isCreating,
        hasSelectedDate: !!selectedDate
      });
    }
  }, [event, employees, isCreating, selectedDate, selectedHour, prefilledParticipants, startTime, endTime]);

  // Filter employees for dropdown
  const filteredEmployees = useMemo(() => {
    if (participantSearch.trim() === '') {
      return [];
    }

    return employees.filter(emp => {
      const searchTerm = participantSearch.toLowerCase();
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase().trim();
      const email = emp.email.toLowerCase();
      
      const isAlreadySelected = selectedParticipants.some(p => p.email === emp.email);
      
      return !isAlreadySelected && (
        email.includes(searchTerm) ||
        fullName.includes(searchTerm) ||
        (emp.first_name && emp.first_name.toLowerCase().includes(searchTerm)) ||
        (emp.last_name && emp.last_name.toLowerCase().includes(searchTerm))
      );
    });
  }, [participantSearch, employees, selectedParticipants]);

  // Participant management functions
  const handleParticipantSelect = (employee) => {
    const newParticipants = [...selectedParticipants, employee];
    setSelectedParticipants(newParticipants);
    setParticipantSearch('');
    setShowParticipantsDropdown(false);
    
    // Auto-save participants immediately for existing events
    if (onSave && event && !isCreating) {
      onSave({ 
        ...event, 
        participants: newParticipants.map(p => p.email) 
      }).catch(error => {
        console.error('Failed to save participants:', error);
        // Revert on error
        setSelectedParticipants(selectedParticipants);
      });
    }
  };

  const handleParticipantRemove = (emailToRemove) => {
    const newParticipants = selectedParticipants.filter(p => p.email !== emailToRemove);
    setSelectedParticipants(newParticipants);
    
    // Auto-save participants immediately for existing events
    if (onSave && event && !isCreating) {
      onSave({ 
        ...event, 
        participants: newParticipants.map(p => p.email) 
      }).catch(error => {
        console.error('Failed to save participants:', error);
        // Revert on error
        setSelectedParticipants(selectedParticipants);
      });
    }
  };

  // Update dropdown visibility
  useEffect(() => {
    setShowParticipantsDropdown(filteredEmployees.length > 0 && participantSearch.trim() !== '');
  }, [filteredEmployees, participantSearch]);

  // Debug logging for selectedParticipants changes
  useEffect(() => {
    console.log('🎭 EventPreview selectedParticipants changed:', selectedParticipants);
  }, [selectedParticipants]);

  const formatTime = (timeString) => {
    if (!timeString) return 'All day';
    return moment(timeString, 'HH:mm:ss').format('h:mm A');
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM D, YYYY');
  };

  const formatTimeRange = () => {
    if (isCreating && selectedDate) {
      try {
        // Ensure selectedDate is a valid Date object
        const validDate = selectedDate instanceof Date && !isNaN(selectedDate) 
          ? selectedDate 
          : new Date(selectedDate);
        
        if (isNaN(validDate)) {
          return 'Invalid date';
        }
        
        const dateString = validDate.toISOString().split('T')[0];
        
        // Better all-day detection: check selectedHour first, then fallback to time strings
        let isAllDayEvent = false;
        
        if (selectedHour === -1) {
          // Explicitly clicked on all-day section
          isAllDayEvent = true;
        } else if (selectedHour !== undefined && selectedHour >= 0) {
          // Valid selectedHour for timed event
          isAllDayEvent = false;
        } else if (event && event.isAllday === true) {
          // Existing all-day event
          isAllDayEvent = true;
        } else if (!editedStartTime || !editedEndTime || editedStartTime.trim() === '' || editedEndTime.trim() === '') {
          // No times specified and no selectedHour - default to all-day
          isAllDayEvent = true;
        }
        
        console.log('🎭 EventPreview isCreating formatTimeRange debug:', {
          selectedHour,
          eventIsAllday: event?.isAllday,
          editedStartTime,
          editedEndTime,
          startTime,
          endTime,
          isAllDayEvent,
          viewMode
        });
        
        // Only show "All Day" in week view
        if (isAllDayEvent && viewMode === 'week') {
          return `${formatDate(dateString)} • All Day`;
        } else if (isAllDayEvent && viewMode === 'day') {
          // Day view - show actual times even for all-day events (12:00 AM - 11:59 PM)
          return `${formatDate(dateString)} 12:00 AM - 11:59 PM`;
        } else if (editedStartTime && editedEndTime) {
          // Show the actual selected/edited times
          return `${formatDate(dateString)} ${formatTime(editedStartTime)} to ${formatTime(editedEndTime)}`;
        } else if (startTime && endTime) {
          // Use the passed startTime and endTime props (correctly calculated from the calendar)
          return `${formatDate(dateString)} ${formatTime(startTime)} to ${formatTime(endTime)}`;
        } else if (selectedHour !== undefined && selectedHour >= 0) {
          // Fallback: Generate time display from selectedHour when times aren't set yet
          const hour = Math.floor(selectedHour);
          const minute = Math.round((selectedHour - hour) * 60);
          const endHour = hour + 1;
          
          const fallbackStartTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          const fallbackEndTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          
          return `${formatDate(dateString)} ${formatTime(fallbackStartTime)} to ${formatTime(fallbackEndTime)}`;
        } else {
          // Fallback - this shouldn't happen in normal flow
          return `${formatDate(dateString)} • All Day`;
        }
      } catch (error) {
        console.error('Error formatting date in formatTimeRange:', error);
        return 'Invalid date';
      }
    }
    
    if (!event) return '';
    
    // Enhanced all-day detection with multiple methods
    const allDayChecks = {
      databaseFlag: event.is_all_day === true,
      eventFlag: event.isAllday === true,
      timeStrings: event.start_time === '00:00' && event.end_time === '23:59',
      dateObjects: event.start && event.end && 
                 moment(event.start).format('HH:mm') === '00:00' && 
                 moment(event.end).format('HH:mm') === '23:59'
    };
    
    const shouldBeAllDay = allDayChecks.databaseFlag || allDayChecks.eventFlag || allDayChecks.timeStrings || allDayChecks.dateObjects;
    
    console.log('🎭 EventPreview all-day check:', {
      event_id: event.id,
      allDayChecks,
      shouldBeAllDay,
      viewMode,
      will_show_all_day: shouldBeAllDay && viewMode === 'week'
    });
    
    // Only show "All Day" in week view, show times in day view
    if (shouldBeAllDay && viewMode === 'week') {
      return 'All Day';
    }
    
    // Show actual times for day view or non-all-day events
    if (event.shift_date && event.start_time && event.end_time) {
      return `${formatDate(event.shift_date)} ${formatTime(event.start_time)} - ${formatTime(event.end_time)}`;
    } else if (event.start && event.end) {
      return `${formatDate(event.start)} ${formatTime(event.start)} - ${formatTime(event.end)}`;
    }
    
    return '';
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowDescriptionOptions(false);
      
      // Auto-save any changes before closing (for existing events)
      if (!isCreating && event && onSave) {
        // Check if any field has been modified
        const hasChanges = 
          editedTitle !== (event.title || '') ||
          editedLocation !== (event.location || '') ||
          editedDescription !== (event.description || '') ||
          editedStartTime !== (event.start_time || '') ||
          editedEndTime !== (event.end_time || '') ||
          JSON.stringify(selectedParticipants.map(p => p.email).sort()) !== 
          JSON.stringify((event.participants || []).sort());
        
        if (hasChanges) {
          // Save all changes
          const updatedData = {
            title: editedTitle,
            location: editedLocation,
            description: editedDescription,
            start_time: editedStartTime,
            end_time: editedEndTime,
            participants: selectedParticipants.map(p => p.email)
          };
          
          onSave({ ...event, ...updatedData }).catch(error => {
            console.error('Failed to auto-save changes:', error);
          });
        }
      }
      
      onClose();
    }
  };

  const handleCreateEvent = async () => {
    if (!isCreating || !onCreate || !selectedDate) return;
    
    if (!editedTitle.trim()) {
      alert('Please enter a title for the event');
      return;
    }

    try {
      // Ensure selectedDate is a valid Date object
      const validDate = selectedDate instanceof Date && !isNaN(selectedDate) 
        ? selectedDate 
        : new Date(selectedDate);
      
      if (isNaN(validDate)) {
        console.error('Invalid selectedDate:', selectedDate);
        alert('Invalid date selected. Please try again.');
        return;
      }
      
      const year = validDate.getFullYear();
      const month = String(validDate.getMonth() + 1).padStart(2, '0');
      const day = String(validDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Format time based on props and edited values
      let eventStartTime = editedStartTime || startTime || null;
      let eventEndTime = editedEndTime || endTime || null;
      let isAllDay = false;
      
      console.log('🎭 EventPreview handleCreateEvent debug:', {
        selectedHour,
        editedStartTime,
        editedEndTime,
        propsStartTime: startTime,
        propsEndTime: endTime,
        eventStartTime,
        eventEndTime,
        viewMode
      });
      
      // Determine if this should be an all-day event - check selectedHour first
      if (selectedHour === -1) {
        // Explicitly clicked on all-day section
        isAllDay = true;
        console.log('🎭 All-day event: selectedHour === -1');
      } else if (event && event.start && event.end && !event.isAllday) {
        // Has valid event with start and end times - use these first
        const startMoment = moment(event.start);
        const endMoment = moment(event.end);
        
        eventStartTime = startMoment.format('HH:mm:ss');
        eventEndTime = endMoment.format('HH:mm:ss');
        isAllDay = false;
        console.log('🎭 Using event times:', { eventStartTime, eventEndTime });
      } else if (eventStartTime && eventEndTime) {
        // Has valid start and end times (either from props or edited values)
        isAllDay = false;
      } else if (selectedHour !== undefined && selectedHour >= 0) {
        // Fallback: Valid selectedHour for timed event - generate times if not already set
        if (!eventStartTime || !eventEndTime || eventStartTime.trim() === '' || eventEndTime.trim() === '') {
          const hour = Math.floor(selectedHour);
          const minute = Math.round((selectedHour - hour) * 60);
          const endHour = hour + 1;
          
          eventStartTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          eventEndTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
        }
        isAllDay = false;
      } else {
        // No selectedHour and no times specified, treat as all-day
        isAllDay = true;
        console.log('🎭 All-day event: no times specified');
      }
      
      // Create base event data without time fields
      const baseEventData = {
        title: editedTitle.trim(),
        description: editedDescription || '',
        shift_date: formattedDate,
        end_date: formattedDate,
        is_all_day: isAllDay,
        location: editedLocation || '',
        calendar_type: 'shift',
        color: '#4263eb',
        participants: selectedParticipants.map(p => p.email),
        recurrence_rule: null,
        timezone: 'America/New_York',
        files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        zoom_info: zoomInfo || undefined,
        resource: selectedEmployee?.id || event?.resource || event?.employee?.id
      };

      // Create start and end Date objects
      let startDate, endDate;
      
      if (isAllDay) {
        // For all-day events, create Date objects with 00:00 and 23:59
        startDate = new Date(validDate);
        startDate.setHours(0, 0, 0, 0);
        
        endDate = new Date(validDate);
        endDate.setHours(23, 59, 59, 999);
      } else {
        // For timed events, parse the time strings and create Date objects
        const [startHour, startMinute] = eventStartTime.split(':').map(Number);
        const [endHour, endMinute] = eventEndTime.split(':').map(Number);
        
        startDate = new Date(validDate);
        startDate.setHours(startHour, startMinute, 0, 0);
        
        endDate = new Date(validDate);
        endDate.setHours(endHour, endMinute, 0, 0);
      }

      // Add time fields and Date objects
      const eventData = {
        ...baseEventData,
        start_time: isAllDay ? '00:00:00' : eventStartTime,
        end_time: isAllDay ? '23:59:59' : eventEndTime,
        start: startDate,
        end: endDate
      };

      console.log('🎭 EventPreview creating eventData:', eventData);
      console.log('🎭 Final event summary:', {
        isAllDay,
        start_time: eventData.start_time,
        end_time: eventData.end_time,
        is_all_day: eventData.is_all_day
      });

      await onCreate(eventData);
      onClose();
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  const handleKeyDown = (e, field) => {
    console.log('🎭 EventPreview handleKeyDown called:', {
      key: e.key,
      field: field,
      editingTitle,
      editingLocation,
      editingTime,
      editingDescription,
      editingParticipants,
      isCreating,
      hasEvent: !!event,
      hasOnDelete: !!onDelete
    });

    if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'title' && isCreating) {
        handleCreateEvent();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTitle(false);
      setEditingLocation(false);
      setEditingTime(false);
      setEditingDescription(false);
      setEditingParticipants(false);
    } else if ((e.key === 'Backspace' || e.key === 'Delete') && !field && !editingTitle && !editingLocation && !editingTime && !editingDescription && !editingParticipants) {
      // Only trigger delete if we're not editing any field
      console.log('🗑️ Delete conditions met - attempting to delete event');
      e.preventDefault();
      if (event && !isCreating && onDelete) {
        console.log('🗑️ Delete key pressed - deleting event:', event.id);
        onDelete(event.id);
        onClose();
      } else {
        console.log('🗑️ Delete blocked:', {
          hasEvent: !!event,
          isCreating,
          hasOnDelete: !!onDelete,
          eventId: event?.id
        });
      }
    }
  };

  // Add missing description options handler
  const handleDescriptionOptionSelect = (option) => {
    setDescriptionMode(option);
    
    if (option === 'attachments') {
      // Open file picker and close options
      fileInputRef.current?.click();
      setShowDescriptionOptions(false);
    } else {
      // For notes and URL, start editing and close options
      setEditingDescription(true);
      setShowDescriptionOptions(false);
      
      if (option === 'url' && !editedDescription.startsWith('http')) {
        setEditedDescription('https://');
      }
    }
  };

  const getDescriptionPlaceholder = () => {
    switch (descriptionMode) {
      case 'notes':
        return 'Add notes or description...';
      case 'url':
        return 'Add URL link...';
      case 'attachments':
        return 'Add attachment description...';
      default:
        return 'Add description...';
    }
  };

  const handleBlur = (field) => {
    // Auto-save on blur for existing events
    if (!isCreating && event && onSave) {
      const updatedData = {};
      
      if (field === 'description') {
        updatedData.description = editedDescription;
      }
      
      if (Object.keys(updatedData).length > 0) {
        onSave({ ...event, ...updatedData }).catch(error => {
          console.error('Failed to save field:', error);
        });
      }
    }
  };

  if (!isOpen || (!event && !isCreating)) return null;

  // Calculate positioning with expansion in mind
  const previewWidth = showDescriptionOptions ? 320 : 280;
  const previewHeight = 300; // Approximate height
  
  let leftPosition, topPosition, showArrow = false;
  
  // Try to position to the right of the clicked event first
  const rightPosition = position.x + 60; // Increased offset from click point
  const hasSpaceOnRight = rightPosition + previewWidth <= window.innerWidth - 20;
  
  if (hasSpaceOnRight) {
    // Position to the right with arrow
    leftPosition = rightPosition;
    showArrow = true;
    // Center vertically relative to the click point
    topPosition = Math.max(20, Math.min(position.y - previewHeight / 2, window.innerHeight - previewHeight - 20));
  } else {
    // Fall back to centered positioning without arrow
    leftPosition = position.x - (previewWidth / 2);
    topPosition = position.y + 10; // Slight offset below cursor
    showArrow = false;
    
    // Ensure the preview doesn't go off-screen
    leftPosition = Math.max(20, Math.min(leftPosition, window.innerWidth - previewWidth - 20));
    topPosition = Math.max(20, Math.min(topPosition, window.innerHeight - previewHeight - 20));
  }

  return ReactDOM.createPortal(
    <div className="event-preview-overlay" onClick={handleOverlayClick} style={{ display: isOpen ? 'block' : 'none' }}>
      <div 
        className={`event-preview ${showDescriptionOptions ? 'expanded' : ''}`}
        style={{
          position: 'absolute',
          left: `${leftPosition}px`,
          top: `${topPosition}px`,
          transform: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        onKeyDown={(e) => handleKeyDown(e)}
        autoFocus
        ref={previewRef}
      >
        {/* Arrow pointing to the clicked event */}
        {showArrow && (
          <div 
            className="preview-arrow"
            style={{
              position: 'absolute',
              left: '-14px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <svg width="14" height="28" viewBox="0 0 14 28" fill="none">
              <path d="M0 14L14 0V28L0 14Z" fill="#E7E7E5" stroke="#C7C7C7" strokeWidth="1"/>
            </svg>
          </div>
        )}

        {/* Header with close and expand buttons */}
        <div className="preview-header">
          <button className="expand-btn" onClick={() => {
            const expandData = event || {
              title: editedTitle,
              location: editedLocation,
              description: editedDescription,
              start: event?.start || (selectedDate && (() => {
                const startDate = new Date(selectedDate);
                if (editedStartTime) {
                  const [hour, minute] = editedStartTime.split(':').map(Number);
                  startDate.setHours(hour, minute, 0, 0);
                } else {
                  startDate.setHours(9, 0, 0, 0);
                }
                return startDate;
              })()),
              end: event?.end || (selectedDate && (() => {
                const endDate = new Date(selectedDate);
                if (editedEndTime) {
                  const [hour, minute] = editedEndTime.split(':').map(Number);
                  endDate.setHours(hour, minute, 0, 0);
                } else {
                  endDate.setHours(10, 0, 0, 0);
                }
                return endDate;
              })()),
              // Include edited times as string properties for EventPopup
              startTime: editedStartTime || (event?.start ? moment(event.start).format('HH:mm') : null),
              endTime: editedEndTime || (event?.end ? moment(event.end).format('HH:mm') : null),
              resource: selectedEmployee?.id || event?.resource || event?.employee?.id,
              employee: selectedEmployee || event?.employee, // Fix: use selectedEmployee for new events
              isNew: isCreating,
              participants: selectedParticipants.map(p => p.email),
              uploadedFiles: uploadedFiles,
              zoomInfo: zoomInfo || event?.zoom_info
            };
            
            console.log('🔄 EventPreview expand button clicked with data:', {
              isCreating,
              selectedEmployee,
              eventEmployee: event?.employee,
              finalEmployee: expandData.employee,
              participantsCount: selectedParticipants.length,
              participants: selectedParticipants.map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }))
            });
            
            onExpand(expandData);
          }} title="Expand">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9"/>
              <polyline points="9 21 3 21 3 15"/>
              <line x1="21" y1="3" x2="14" y2="10"/>
              <line x1="3" y1="21" x2="10" y2="14"/>
            </svg>
          </button>
          
          <button className="close-btn" onClick={onClose} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Title Section */}
        <div className="preview-section title-section">
          {editingTitle ? (
            <div className="edit-field-container">
              <input
                type="text"
                className="edit-input title-edit"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'title')}
                placeholder="Add title"
                autoFocus
              />
            </div>
          ) : (
            <div 
              className="preview-field title-field"
              onClick={() => setEditingTitle(true)}
            >
              <h3 className="event-title">{(event?.title || editedTitle) || (isCreating ? 'New Event' : 'Untitled Event')}</h3>
            </div>
          )}
        </div>

        {/* Time Section */}
        <div className="preview-section time-section">
          <div 
            className="preview-field time-field"
            onClick={() => setEditingTime(true)}
          >
            <span className="field-text">{formatTimeRange()}</span>
          </div>
        </div>

        {/* Location Section */}
        <div className="preview-section location-section">
          <div className="preview-field location-field">
            <div className="location-content">
              <div className="location-input-container">
                <input
                  type="text"
                  value={editedLocation}
                  onChange={(e) => setEditedLocation(e.target.value)}
                  placeholder="Add Location or Video Call"
                  className="field-input"
                />
                <div className="video-call-icon">
                  <button
                    onClick={createZoomMeeting}
                    disabled={isCreatingZoom}
                    className="video-call-button"
                    title="Create Zoom meeting"
                  >
                    {isCreatingZoom ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <svg width="16" height="10" viewBox="0 0 20 13" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Display Zoom meeting details if available */}
              {(zoomInfo || event?.zoom_info) && (
                <div className="zoom-meeting-details">
                  <div className="zoom-meeting-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 10.5V7C17 6.73478 16.8946 6.48043 16.7071 6.29289C16.5196 6.10536 16.2652 6 16 6H4C3.73478 6 3.48043 6.10536 3.29289 6.29289C3.10536 6.48043 3 6.73478 3 7V17C3 17.2652 3.10536 17.5196 3.29289 17.7071C3.48043 17.8946 3.73478 18 4 18H16C16.2652 18 16.5196 17.8946 16.7071 17.7071C16.8946 17.5196 17 17.2652 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="#0B5CFF"/>
                    </svg>
                    <span className="zoom-meeting-title">Zoom Meeting</span>
                  </div>
                  <div className="zoom-meeting-info">
                    <div className="zoom-info-item">
                      <span className="zoom-info-label">Meeting ID:</span>
                      <span className="zoom-info-value">{(zoomInfo || event?.zoom_info)?.id}</span>
                    </div>
                    {(zoomInfo || event?.zoom_info)?.passcode && (
                      <div className="zoom-info-item">
                        <span className="zoom-info-label">Passcode:</span>
                        <span className="zoom-info-value">{(zoomInfo || event?.zoom_info)?.passcode}</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => window.open((zoomInfo || event?.zoom_info)?.joinUrl, '_blank')}
                    className="zoom-join-button"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Join Meeting
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Participants Section */}
        <div className="preview-section participants-section">
          <div className="preview-field participants-field">
            {editingParticipants ? (
              <div className="participants-inline-edit">
                <div className="participants-search-wrapper">
                  <input
                    ref={participantInputRef}
                    type="text"
                    className="participant-search-simple"
                    value={participantSearch}
                    onChange={(e) => setParticipantSearch(e.target.value)}
                    placeholder="Add participants..."
                    autoFocus
                  />
                  
                  {showParticipantsDropdown && filteredEmployees.length > 0 && (
                    <div className="participants-dropdown-simple">
                      {filteredEmployees.map((employee) => (
                        <div
                          key={employee.email}
                          className="participant-item-simple"
                          onClick={() => {
                            handleParticipantSelect(employee);
                            setEditingParticipants(false);
                            setParticipantSearch('');
                          }}
                        >
                          {employee.first_name && employee.last_name 
                            ? `${employee.first_name} ${employee.last_name}`
                            : employee.email
                          }
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span 
                className="field-text"
                onClick={() => setEditingParticipants(true)}
              >
                {selectedParticipants.length > 0 
                  ? `${selectedParticipants.length} participant${selectedParticipants.length > 1 ? 's' : ''}`
                  : 'Add Participants'
                }
              </span>
            )}
          </div>
          
          {/* Show selected participants below */}
          {selectedParticipants.length > 0 && (
            <div className="selected-participants-list">
              {selectedParticipants.map((participant) => (
                <div key={participant.email} className="selected-participant-item">
                  <span className="participant-name">
                    {participant.first_name && participant.last_name 
                      ? `${participant.first_name} ${participant.last_name}`
                      : participant.email
                    }
                  </span>
                  <button
                    type="button"
                    className="remove-participant-btn"
                    onClick={() => handleParticipantRemove(participant.email)}
                    title="Remove participant"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="preview-section description-section">
          {!showDescriptionOptions && !editingDescription ? (
            <div 
              className="preview-field description-field"
              onClick={() => setShowDescriptionOptions(true)}
            >
              <span className="field-text">
                {(event?.description || editedDescription) || 'Add notes, URL or attachments'}
              </span>
            </div>
          ) : null}
          
          {showDescriptionOptions && !editingDescription ? (
            <>
              {/* Add Notes Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('notes')}
              >
                <span className="field-text">Add Notes</span>
              </div>
              
              {/* Add URL Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('url')}
              >
                <span className="field-text">Add URL</span>
              </div>
              
              {/* Add Attachment Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('attachments')}
              >
                <span className="field-text">Add Attachment...</span>
              </div>
            </>
          ) : null}
          
          {/* Show editing field when in edit mode */}
          {editingDescription && (
            <div className="edit-field-container">
              <textarea
                className="edit-input description-edit"
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={() => {
                  if (!isCreating) handleBlur('description');
                  setEditingDescription(false);
                  setShowDescriptionOptions(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setEditingDescription(false);
                    setShowDescriptionOptions(false);
                  } else {
                    handleKeyDown(e, 'description');
                  }
                }}
                placeholder={getDescriptionPlaceholder()}
                rows={2}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {/* Uploaded Files Section */}
        {uploadedFiles.length > 0 && (
          <div className="preview-section files-section">
            <div className="files-list">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="file-item-preview">
                  <div className="file-info">
                    <span className="file-name">{file.originalName || file.name}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                  <div className="file-actions">
                    <button
                      type="button"
                      className="download-file-btn"
                      onClick={() => downloadFile(file, index)}
                      title="Download file"
                    >
                      ⬇
                    </button>
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => removeFile(index)}
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EventPreview; 